import { Static, Type } from '@sinclair/typebox';
import { AiFieldKind } from './ai-assist';
import { EmailConfigSchema } from './email_types/EmailTypes';
import {
  CatalogSelectionResponseSchema,
  SupplementOptionSchema,
  SupplementQuantitiesSchema,
} from './catalog-selection';
import { CroppableImageSchema, CroppableImageResponseSchema } from './image';
import { PackTypeSchema } from './translation_namespace/pack_namespace';
import { PageTypeEnum } from './translation_namespace/page_type_namespace';
import { SortConfig, SortDirection } from './filter/sort';
import {
  VoucherType,
  VoucherTypeSchema,
  VoucherUnusableReasonSchema,
  VoucherUsageSourceSchema,
  VoucherUsageKindSchema,
} from './translation_namespace/voucher_namespace';
import { TrackingItemSchema } from './payment_infos';

// ─────────────────────────────────────────
// Bornes carte cadeau (centimes) — source unique partagée back/front (évite les "100" en dur dupliqués).
// ─────────────────────────────────────────
/** Montant minimum d'une carte cadeau en montant libre : 1 €. */
export const GIFT_CARD_AMOUNT_MIN_CENTS = 100;
/** Plafond défensif du montant total d'un achat carte cadeau : 100 000 € (anti-abus). */
export const GIFT_CARD_AMOUNT_MAX_CENTS = 100_000_00;
/** Quantité max par prestation dans une carte cadeau (borne anti-abus). */
export const GIFT_CARD_MAX_QUANTITY_PER_ITEM = 50;
/** Nombre max de prestations distinctes dans une carte cadeau (borne anti-abus). */
export const GIFT_CARD_MAX_ITEMS = 20;

/**
 * Chemin public (relatif) du PDF d'une carte cadeau — source unique partagée back (buildDownloadUrl)
 * et front (VoucherDetailModal / GiftCardConfirmation). Correspond à la route
 * `@Controller('public/gift-card')` + `@Get('voucher/:uuid/pdf')`.
 */
export const giftCardPdfPublicPath = (uuid: string): string => `/public/gift-card/voucher/${uuid}/pdf`;

/**
 * Chemin public (relatif à `API_ENDPOINT`) du PDF de CGV d'un pro : redirige (302) vers une URL S3
 * présignée fraîche. Backend : `@Controller('public/gift-card')` + `@Get(':slug/terms')`.
 */
export const giftCardTermsPublicPath = (slug: string): string => `/public/gift-card/${encodeURIComponent(slug)}/terms`;

/**
 * Absolutise un chemin public de l'API (`giftCardPdfPublicPath`, `giftCardTermsPublicPath`) contre
 * la base API du contexte appelant (`public_env.API_ENDPOINT` côté front, `CONFIGURATION_ENV.API_ENDPOINT`
 * côté back).
 *
 * ⚠️ Ces chemins sont RELATIFS : posés tels quels dans un `href`, le navigateur les résout sur
 * l'origine de la PAGE (site du pro, app pro) et non sur l'API → 404 en preprod/prod. En local le
 * bug est invisible, `frontend-web` proxifiant `/public/*` vers le backend. Passer systématiquement
 * par ce helper est ce qui rend l'invariant vérifiable.
 */
export const toApiAbsoluteUrl = (apiEndpoint: string, path: string): string =>
  `${apiEndpoint.replace(/\/+$/, '')}${path}`;

/**
 * Ajoute `months` mois à une date en CLAMPANT au dernier jour du mois cible (évite le débordement
 * natif de `Date.setMonth` : 31/01 + 1 mois donnerait le 03/03 au lieu du 28/02). Heures/minutes
 * préservées. Source unique partagée (checkout + activation du bon).
 */
export const addMonthsClamped = (date: Date, months: number): Date => {
  const result = new Date(date.getTime());
  const targetDay = result.getDate();
  result.setDate(1); // neutralise le débordement pendant le changement de mois
  result.setMonth(result.getMonth() + months);
  const lastDayOfTargetMonth = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(targetDay, lastDayOfTargetMonth));
  return result;
};

/**
 * Un forfait (voucher type PACK) acheté POUR SOI (non coché « cadeau »). Seul effet : un vocabulaire
 * neutre (« Votre achat est confirmé », « Voir mon forfait ») au lieu du vocabulaire carte cadeau, dans
 * la confirmation et l'email acheteur. Il a son PDF comme tous les bons : les pros envoient eux-mêmes
 * le PDF d'un bon à leur client, cadeau ou non. Source unique partagée (bindings email, confirmation).
 */
export const isPlainPackPurchase = (v: { type?: string | null; is_gift?: boolean | null }): boolean =>
  v.type === VoucherType.PACK && !v.is_gift;

// ─────────────────────────────────────────
// Présélection du tunnel d'achat (deep-link carte cadeau / forfait)
// ─────────────────────────────────────────
/** Type d'achat présélectionné à l'ouverture du tunnel (aligné sur `PurchaseType` du PurchaseTunnel). */
export type GiftTunnelPurchaseType = 'AMOUNT' | 'PACK' | 'PRESTATION';

/** Résultat : présélection à injecter dans `<PurchaseTunnel preselect scope />`. */
export interface GiftTunnelSelection {
  preselect?: { type?: GiftTunnelPurchaseType; packId?: number; prestationId?: number; durationMinutes?: number };
  scope: 'full' | 'pack';
}

/** Entrées (deep-link `?pack=`/`?prestation=`/`?packs` + type de la page hôte). */
export interface GiftTunnelSelectionInput {
  /** `?pack=<id>` → achat direct d'un forfait précis (scope pack). */
  packId?: number | null;
  /** `?prestation=<id>` → tunnel cadeau complet positionné sur cette prestation. */
  prestationId?: number | null;
  /** `?duration=<min>` → variante de durée choisie pour la prestation présélectionnée. */
  durationMinutes?: number | null;
  /** `?packs` → onglet Forfaits sans forfait présélectionné (scope pack). */
  packChoice?: boolean;
  /** Type de la page qui héberge le bloc (fallback quand aucun deep-link : page Forfaits → pack). */
  pageType?: string;
}

/**
 * Résout la présélection + le scope du tunnel d'achat depuis les deep-links (`?pack`/`?prestation`/`?packs`)
 * puis, à défaut, depuis le type de la page hôte (page `pack` → scope pack). Source unique partagée
 * par la page hébergée `GiftCardPageClient` (frontend-web) ET le bloc `gift-card` des sites custom.
 */
export function resolveGiftTunnelSelection(input: GiftTunnelSelectionInput): GiftTunnelSelection {
  const { packId, prestationId, durationMinutes, packChoice, pageType } = input;
  if (packId != null) return { preselect: { type: 'PACK', packId }, scope: 'pack' };
  if (prestationId != null)
    return {
      preselect: { type: 'PRESTATION', prestationId, ...(durationMinutes != null ? { durationMinutes } : {}) },
      scope: 'full',
    };
  if (packChoice) return { preselect: { type: 'PACK' }, scope: 'pack' };
  if (pageType === PageTypeEnum.PACK) return { scope: 'pack' };
  return { scope: 'full' };
}

// ─────────────────────────────────────────
// PACK (template) Schemas
// ─────────────────────────────────────────

export const PackPrestationInputSchema = Type.Object({
  prestation_id: Type.Integer({
    minimum: 1,
    meta: { input_type: 'prestation_selector' },
    placeholder: 'INPUT_PLACEHOLDER_PACK_PRESTATION',
    title: 'INPUT_TITLE_PACK_PRESTATION',
  }),
  quantity: Type.Integer({
    minimum: 1,
    placeholder: 'INPUT_PLACEHOLDER_PACK_QUANTITY',
    title: 'INPUT_TITLE_PACK_QUANTITY',
  }),
  selected_duration_minutes: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
});
export type PackPrestationInputObject = Static<typeof PackPrestationInputSchema>;

export const CreatePackRequestSchema = Type.Object({
  name: Type.String({
    minLength: 1,
    maxLength: 100,
    placeholder: 'INPUT_PLACEHOLDER_PACK_NAME',
    title: 'INPUT_TITLE_PACK_NAME',
  }),
  description: Type.Optional(
    Type.String({
      maxLength: 500,
      meta: { multiline: true, ai: { kind: AiFieldKind.SHORT_DESCRIPTION } },
      placeholder: 'INPUT_PLACEHOLDER_PACK_DESCRIPTION',
      title: 'INPUT_TITLE_PACK_DESCRIPTION',
    }),
  ),
  type: PackTypeSchema({
    placeholder: 'INPUT_PLACEHOLDER_PACK_TYPE',
    title: 'INPUT_TITLE_PACK_TYPE',
    meta: { multiple: false, disable_search: true },
  }),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 5,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PACK_PICTURES',
      title: 'INPUT_TITLE_PACK_PICTURES',
    }),
  ),
  price: Type.Integer({
    minimum: 50,
    meta: { is_price: true },
    placeholder: 'INPUT_PLACEHOLDER_PACK_PRICE',
    title: 'INPUT_TITLE_PACK_PRICE',
  }),
  validity_months: Type.Integer({
    minimum: 1,
    placeholder: 'INPUT_PLACEHOLDER_PACK_VALIDITY',
    title: 'INPUT_TITLE_PACK_VALIDITY',
  }),
  multiple_usage: Type.Boolean({
    default: false,
    placeholder: 'INPUT_PLACEHOLDER_PACK_MULTIPLE_USAGE',
    title: 'INPUT_TITLE_PACK_MULTIPLE_USAGE',
  }),
  is_active: Type.Boolean({
    default: false,
    placeholder: 'INPUT_PLACEHOLDER_PACK_ACTIVE',
    title: 'INPUT_TITLE_PACK_ACTIVE',
  }),
  prestations: Type.Array(PackPrestationInputSchema, {
    minItems: 1,
    placeholder: 'INPUT_PLACEHOLDER_PACK_PRESTATION',
    title: 'INPUT_TITLE_PACK_PRESTATIONS',
    meta: { input_type: 'pack_prestations_selector' },
  }),
});
export type CreatePackRequestObject = Static<typeof CreatePackRequestSchema>;

export const UpdatePackRequestSchema = Type.Object({
  name: Type.Optional(
    Type.String({
      minLength: 1,
      maxLength: 100,
      placeholder: 'INPUT_PLACEHOLDER_PACK_NAME',
      title: 'INPUT_TITLE_PACK_NAME',
    }),
  ),
  description: Type.Optional(
    Type.String({
      maxLength: 500,
      meta: { multiline: true, ai: { kind: AiFieldKind.SHORT_DESCRIPTION } },
      placeholder: 'INPUT_PLACEHOLDER_PACK_DESCRIPTION',
      title: 'INPUT_TITLE_PACK_DESCRIPTION',
    }),
  ),
  type: Type.Optional(
    PackTypeSchema({
      placeholder: 'INPUT_PLACEHOLDER_PACK_TYPE',
      title: 'INPUT_TITLE_PACK_TYPE',
      meta: { multiple: false, disable_search: true },
    }),
  ),
  pictures: Type.Optional(
    Type.Array(CroppableImageSchema, {
      minItems: 0,
      maxItems: 5,
      meta: { picture: true, multiple: true, crop_ratio: 3 / 2 },
      placeholder: 'INPUT_PLACEHOLDER_PACK_PICTURES',
      title: 'INPUT_TITLE_PACK_PICTURES',
    }),
  ),
  price: Type.Optional(
    Type.Integer({
      minimum: 50,
      meta: { is_price: true },
      placeholder: 'INPUT_PLACEHOLDER_PACK_PRICE',
      title: 'INPUT_TITLE_PACK_PRICE',
    }),
  ),
  validity_months: Type.Optional(
    Type.Integer({
      minimum: 1,
      placeholder: 'INPUT_PLACEHOLDER_PACK_VALIDITY',
      title: 'INPUT_TITLE_PACK_VALIDITY',
    }),
  ),
  multiple_usage: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PACK_MULTIPLE_USAGE',
      title: 'INPUT_TITLE_PACK_MULTIPLE_USAGE',
    }),
  ),
  is_active: Type.Optional(
    Type.Boolean({
      placeholder: 'INPUT_PLACEHOLDER_PACK_ACTIVE',
      title: 'INPUT_TITLE_PACK_ACTIVE',
    }),
  ),
  prestations: Type.Optional(
    Type.Array(PackPrestationInputSchema, {
      minItems: 1,
      placeholder: 'INPUT_PLACEHOLDER_PACK_PRESTATION',
      title: 'INPUT_TITLE_PACK_PRESTATIONS',
      meta: { input_type: 'pack_prestations_selector' },
    }),
  ),
});
export type UpdatePackRequestObject = Static<typeof UpdatePackRequestSchema>;

// Ordre d'affichage des packs (back-office, tunnel carte cadeau, site). `ordered_ids` peut n'être
// qu'une partie des packs du pro (liste paginée) : les ids envoyés sont permutés entre eux, les
// autres gardent leur place.
export const ReorderPacksRequestSchema = Type.Object({
  ordered_ids: Type.Array(Type.Integer({ minimum: 1 }), { minItems: 1 }),
});
export type ReorderPacksRequestObject = Static<typeof ReorderPacksRequestSchema>;

export const PackPrestationResponseSchema = CatalogSelectionResponseSchema;

export const PackResponseSchema = Type.Object({
  pack_id: Type.Number(),
  name: Type.String(),
  description: Type.Optional(Type.String()),
  type: Type.String(),
  // `CroppableImageResponseSchema` (et NON un objet `{ small_url, medium_url, large_url }`) : indispensable
  // pour que l'édition fonctionne. `transformToSchema` y émet la **clé S3 brute** `url` (+ `preview_url`
  // pour la miniature). Sans `url`, le widget d'upload re-soumet `{ url: undefined }` → la photo n'est
  // jamais enregistrée (cf. ImageUploadInput / même forme que prestations & catégories).
  pictures: Type.Optional(Type.Array(CroppableImageResponseSchema)),
  price: Type.Number(),
  price_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  validity_months: Type.Number(),
  multiple_usage: Type.Boolean(),
  is_active: Type.Boolean(),
  position: Type.Number(),
  prestations: Type.Array(PackPrestationResponseSchema),
  catalog_value: Type.Optional(Type.Number()),
  catalog_value_string: Type.Optional(Type.String({ meta: { is_price: true } })),
  created_at: Type.Optional(Type.String()),
});
export type PackResponseObject = Static<typeof PackResponseSchema>;

export const PackListResponseSchema = Type.Object({
  has_elements: Type.Boolean(),
  total: Type.Number(),
  items: Type.Array(PackResponseSchema),
});
export type PackListResponseObject = Static<typeof PackListResponseSchema>;

/**
 * Tri de la liste des packs du catalogue (`/catalog/packs`). Défaut : `position` croissante + tiebreaker
 * `pack_id` croissant. Changement volontaire : l'ancien `{ position: 'asc' }` seul n'était pas
 * déterministe à position égale (pagination par offset).
 */
export const PACK_SORTS: SortConfig = {
  fields: {
    // Seule définition de l'ordre personnalisé des packs : le backend en dérive PACK_DISPLAY_ORDER
    // (tunnel carte cadeau, site). Clé `default` conservée : c'est la valeur de `sort_by` déjà exposée.
    default: {
      label: 'SORT_FIELD_MANUAL',
      value_type: 'fixed',
      default_direction: SortDirection.ASC,
      order_by: (): Record<string, unknown>[] => [{ position: SortDirection.ASC }],
      api_description:
        "Ordre personnalisé réglé par le pro par glisser-déposer (celui du tunnel d'achat et du site ; un nouveau pack arrive en fin de liste). Sens unique (sort_dir ignoré). C'est l'ordre par défaut.",
    },
    name: {
      label: 'SORT_FIELD_NAME',
      value_type: 'text',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ name: direction }],
      api_description: 'Nom du pack, par ordre alphabétique.',
    },
    price: {
      label: 'SORT_FIELD_PRICE',
      value_type: 'number',
      default_direction: SortDirection.ASC,
      order_by: (direction: SortDirection): Record<string, unknown>[] => [{ price: direction }],
      api_description: 'Prix de vente du pack, en centimes.',
    },
  },
  default_field: 'default',
  tiebreaker: 'pack_id',
};

// ─────────────────────────────────────────
// GIFT CARD SETTINGS Schemas
// ─────────────────────────────────────────

export const GiftCardSettingsRequestSchema = Type.Object({
  allow_amount: Type.Boolean({
    default: false,
    title: 'INPUT_TITLE_GIFT_ALLOW_AMOUNT',
  }),
  amount_min: Type.Optional(
    Type.Integer({
      minimum: GIFT_CARD_AMOUNT_MIN_CENTS,
      title: 'INPUT_TITLE_GIFT_AMOUNT_MIN',
      placeholder: 'INPUT_PLACEHOLDER_GIFT_AMOUNT_MIN',
      meta: { is_price: true, visible_when: { field: 'allow_amount', value: true } },
    }),
  ),
  amount_max: Type.Optional(
    Type.Integer({
      minimum: GIFT_CARD_AMOUNT_MIN_CENTS,
      title: 'INPUT_TITLE_GIFT_AMOUNT_MAX',
      placeholder: 'INPUT_PLACEHOLDER_GIFT_AMOUNT_MAX',
      meta: { is_price: true, visible_when: { field: 'allow_amount', value: true } },
    }),
  ),
  allow_prestations: Type.Boolean({
    default: false,
    meta: { hidden: true },
  }),
  allowed_prestation_ids: Type.Array(Type.Integer(), {
    default: [],
    meta: { hidden: true },
  }),
  allowed_category_ids: Type.Array(Type.Integer(), {
    default: [],
    meta: { hidden: true },
  }),
  allow_packs: Type.Boolean({
    default: false,
    meta: { hidden: true },
  }),
  default_validity_months: Type.Integer({
    minimum: 1,
    maximum: 60,
    default: 6,
    title: 'INPUT_TITLE_GIFT_DEFAULT_VALIDITY',
    placeholder: 'INPUT_PLACEHOLDER_GIFT_DEFAULT_VALIDITY',
  }),
  default_multiple_usage: Type.Boolean({
    default: true,
    title: 'INPUT_TITLE_GIFT_DEFAULT_MULTIPLE_USAGE',
  }),
  restore_voucher_on_cancel: Type.Boolean({
    default: true,
    title: 'INPUT_TITLE_GIFT_RESTORE_VOUCHER_ON_CANCEL',
  }),
  /**
   * Template `EmailConfig` du PDF de carte cadeau, édité par le pro via l'éditeur d'emails réutilisé.
   * Typé via `EmailConfigSchema` (et non `Type.Unknown`) : indispensable car le pipe AJV utilise
   * `removeAdditional: 'all'`, qui viderait le contenu d'un schéma `{}` (Unknown). `components[].values`
   * = `Record(String, Unknown)` (patternProperties) → le HTML des blocs est préservé.
   */
  pdf_template_config: Type.Optional(EmailConfigSchema),
});
export type GiftCardSettingsRequestObject = Static<typeof GiftCardSettingsRequestSchema>;

export const GiftCardSettingsResponseSchema = Type.Object({
  gift_card_settings_id: Type.Number(),
  allow_amount: Type.Boolean(),
  amount_min: Type.Optional(Type.Number()),
  amount_max: Type.Optional(Type.Number()),
  allow_prestations: Type.Boolean(),
  allowed_prestation_ids: Type.Array(Type.Integer()),
  allowed_category_ids: Type.Array(Type.Integer()),
  allow_packs: Type.Boolean(),
  default_validity_months: Type.Number(),
  default_multiple_usage: Type.Boolean(),
  restore_voucher_on_cancel: Type.Boolean(),
  /** Slug public du pro — pour construire le lien partageable du tunnel d'achat de carte cadeau. */
  professional_slug: Type.Optional(Type.String()),
  /** Template `EmailConfig` du PDF de carte cadeau (édité via l'éditeur d'emails). */
  pdf_template_config: Type.Optional(Type.Unknown()),
  /** Nom du fichier PDF de CGV uploadé par le pro (absent si aucun). */
  terms_pdf_name: Type.Optional(Type.String()),
});
export type GiftCardSettingsResponseObject = Static<typeof GiftCardSettingsResponseSchema>;

/** Aperçu du PDF de carte cadeau (éditeur) : le vrai rendu, avec des données d'exemple. */
export const GiftCardPdfPreviewRequestSchema = Type.Object({
  /** Template en cours d'édition, pas encore enregistré. Absent = template enregistré du pro (ou défaut). */
  pdf_template_config: Type.Optional(EmailConfigSchema),
});
export type GiftCardPdfPreviewRequestObject = Static<typeof GiftCardPdfPreviewRequestSchema>;

export const GiftCardPdfPreviewResponseSchema = Type.Object({
  /** PDF généré, encodé en base64. */
  pdf_base64: Type.String(),
});
export type GiftCardPdfPreviewResponseObject = Static<typeof GiftCardPdfPreviewResponseSchema>;

// ─────────────────────────────────────────
// VOUCHER Schemas
// ─────────────────────────────────────────

export const VoucherItemInputSchema = Type.Object({
  prestation_id: Type.Integer({
    minimum: 1,
    meta: { input_type: 'prestation_selector' },
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_PRESTATION',
    title: 'INPUT_TITLE_VOUCHER_PRESTATION',
  }),
  quantity: Type.Integer({
    minimum: 1,
    maximum: GIFT_CARD_MAX_QUANTITY_PER_ITEM,
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_QUANTITY',
    title: 'INPUT_TITLE_VOUCHER_QUANTITY',
  }),
  selected_duration_minutes: Type.Optional(Type.Integer({ minimum: 1 })),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
});

export const CreateVoucherRequestSchema = Type.Object({
  internal_name: Type.Optional(
    Type.String({
      maxLength: 100,
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_NAME',
      title: 'INPUT_TITLE_VOUCHER_NAME',
    }),
  ),
  type: VoucherTypeSchema({
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_TYPE',
    title: 'INPUT_TITLE_VOUCHER_TYPE',
    meta: { multiple: false, disable_search: true },
  }),
  pro_customer_id: Type.Integer({
    minimum: 1,
    meta: { input_type: 'pro_customer_selector', auto_fill_gift: true },
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_CUSTOMER',
    title: 'INPUT_TITLE_VOUCHER_CUSTOMER',
  }),
  pack_id: Type.Optional(
    Type.Integer({
      minimum: 1,
      meta: {
        input_type: 'pack_selector',
        visible_when: { field: 'type', value: 'PACK' },
        show_manage_link_when_empty: true,
      },
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_PACK',
      title: 'INPUT_TITLE_VOUCHER_PACK',
    }),
  ),
  prestations: Type.Optional(
    Type.Array(VoucherItemInputSchema, {
      minItems: 1,
      maxItems: GIFT_CARD_MAX_ITEMS,
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_PRESTATION',
      title: 'INPUT_TITLE_VOUCHER_PRESTATIONS',
      meta: {
        input_type: 'pack_prestations_selector',
        visible_when: { field: 'type', value: 'PRESTATION' },
      },
    }),
  ),
  amount: Type.Optional(
    Type.Integer({
      minimum: GIFT_CARD_AMOUNT_MIN_CENTS,
      meta: { is_price: true, visible_when: { field: 'type', value: 'AMOUNT' } },
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_AMOUNT',
      title: 'INPUT_TITLE_VOUCHER_AMOUNT',
    }),
  ),
  multiple_usage: Type.Boolean({
    default: true,
    title: 'INPUT_TITLE_VOUCHER_MULTIPLE_USAGE',
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_MULTIPLE_USAGE',
  }),
  expiration_date: Type.String({
    format: 'date-time',
    placeholder: 'INPUT_PLACEHOLDER_VOUCHER_EXPIRATION',
    title: 'INPUT_TITLE_VOUCHER_EXPIRATION',
    meta: {
      min_date_offset_days: 0,
      date_shortcuts: [
        { label: '1MONTH', months: 1 },
        { label: '3MONTHS', months: 3 },
        { label: '6MONTHS', months: 6 },
        { label: '12MONTHS', months: 12 },
      ],
    },
  }),
  is_gift: Type.Optional(
    Type.Boolean({
      default: false,
      title: 'INPUT_TITLE_VOUCHER_IS_GIFT',
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_IS_GIFT',
    }),
  ),
  gift_recipient_name: Type.Optional(
    Type.String({
      maxLength: 100,
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_GIFT_NAME',
      title: 'INPUT_TITLE_VOUCHER_GIFT_NAME',
      meta: { visible_when: { field: 'is_gift', value: true } },
    }),
  ),
  gift_recipient_email: Type.Optional(
    Type.String({
      maxLength: 255,
      format: 'email',
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_GIFT_EMAIL',
      title: 'INPUT_TITLE_VOUCHER_GIFT_EMAIL',
      meta: { visible_when: { field: 'is_gift', value: true } },
    }),
  ),
  gift_recipient_phone: Type.Optional(
    Type.String({
      maxLength: 20,
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_GIFT_PHONE',
      title: 'INPUT_TITLE_VOUCHER_GIFT_PHONE',
      meta: { visible_when: { field: 'is_gift', value: true } },
    }),
  ),
  gift_message: Type.Optional(
    Type.String({
      maxLength: 500,
      meta: {
        multiline: true,
        visible_when: { field: 'is_gift', value: true },
        ai: { kind: AiFieldKind.PERSONAL_MESSAGE },
      },
      placeholder: 'INPUT_PLACEHOLDER_VOUCHER_GIFT_MESSAGE',
      title: 'INPUT_TITLE_VOUCHER_GIFT_MESSAGE',
    }),
  ),
});
export type CreateVoucherRequestObject = Static<typeof CreateVoucherRequestSchema>;

export const UseVoucherItemSchema = Type.Object({
  voucher_item_id: Type.Integer({ minimum: 1 }),
  quantity: Type.Integer({ minimum: 1 }),
});

export const UseVoucherRequestSchema = Type.Object({
  items: Type.Optional(Type.Array(UseVoucherItemSchema, { minItems: 1 })),
  amount: Type.Optional(Type.Integer({ minimum: 1 })),
  appointment_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type UseVoucherRequestObject = Static<typeof UseVoucherRequestSchema>;

export const PreviewUseVoucherRequestSchema = Type.Object({
  unique_code: Type.String({ minLength: 1, maxLength: 30 }),
  amount: Type.Optional(Type.Integer({ minimum: 1 })),
  appointment_id: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type PreviewUseVoucherRequestObject = Static<typeof PreviewUseVoucherRequestSchema>;

/**
 * Recharge manuelle (top-up) d'un bon par le pro. `amount` pour les bons AMOUNT,
 * `items` pour les bons PACK/PRESTATION. Le top-up peut dépasser la valeur initiale
 * (garde-fou anti-abus côté backend : GIFT_CARD_AMOUNT_MAX_CENTS / GIFT_CARD_MAX_QUANTITY_PER_ITEM).
 */
export const RechargeVoucherRequestSchema = Type.Object({
  amount: Type.Optional(Type.Integer({ minimum: 1, meta: { is_price: true } })),
  items: Type.Optional(Type.Array(UseVoucherItemSchema, { minItems: 1 })),
});
export type RechargeVoucherRequestObject = Static<typeof RechargeVoucherRequestSchema>;

export const VoucherItemResponseSchema = Type.Object({
  voucher_item_id: Type.Number(),
  prestation_name: Type.String(),
  prestation_color: Type.Optional(Type.String()),
  prestation_id: Type.Optional(Type.Number()),
  duration_minutes: Type.Optional(Type.Number()),
  initial_quantity: Type.Number(),
  remaining_quantity: Type.Number(),
  selected_supplement_ids: Type.Optional(Type.Array(Type.Integer())),
  supplement_quantities: Type.Optional(SupplementQuantitiesSchema),
  supplements: Type.Optional(Type.Array(SupplementOptionSchema)),
});

export const VoucherUsageItemResponseSchema = Type.Object({
  prestation_name: Type.String(),
  quantity_used: Type.Number(),
});

export const VoucherUsageResponseSchema = Type.Object({
  voucher_usage_id: Type.Number(),
  amount_used: Type.Optional(Type.Number()),
  amount_used_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount_used' } })),
  source: Type.Optional(VoucherUsageSourceSchema()),
  // CONSUMPTION (débit) ou MANUAL_RECHARGE (recharge) — pilote l'affichage distinct dans l'historique.
  kind: Type.Optional(VoucherUsageKindSchema()),
  is_cancelled: Type.Optional(Type.Boolean()),
  cancelled_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'deleted_at', with_time: true } }),
  ),
  items: Type.Optional(Type.Array(VoucherUsageItemResponseSchema)),
  appointment_id: Type.Optional(Type.Number()),
  appointment_start_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'appointment_start', with_time: true } }),
  ),
  used_by_email: Type.Optional(Type.String()),
  used_at: Type.String(),
  used_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'used_at', with_time: true } }),
  ),
});

export const VoucherResponseSchema = Type.Object({
  voucher_id: Type.Number(),
  uuid: Type.String(),
  unique_code: Type.String(),
  internal_name: Type.Optional(Type.String()),
  type: Type.String(),
  status: Type.String(),
  multiple_usage: Type.Boolean(),
  expiration_date: Type.String(),
  validity_months: Type.Number(),
  initial_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'initial_amount' } })),
  remaining_amount: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'remaining_amount' } })),
  /** Solde restant brut (unité mineure) : sert à plafonner un montant saisi, jamais à l'affichage. */
  remaining_amount_raw: Type.Optional(Type.Number()),
  /** Motif pour lequel le bon n'est plus utilisable (date incluse) ; absent s'il l'est. Le pro est averti, pas bloqué. */
  unusable_reason: Type.Optional(VoucherUnusableReasonSchema()),
  usage_percentage: Type.Optional(Type.Number()),
  pack_id: Type.Optional(Type.Number()),
  pack_name: Type.Optional(Type.String()),
  pack_price: Type.Optional(Type.Number()),
  is_gift: Type.Boolean(),
  gift_recipient_name: Type.Optional(Type.String()),
  gift_recipient_email: Type.Optional(Type.String()),
  gift_recipient_phone: Type.Optional(Type.String()),
  gift_message: Type.Optional(Type.String()),
  is_created_by_pro: Type.Boolean(),
  pro_customer_id: Type.Number(),
  pro_customer_firstname: Type.Optional(Type.String()),
  pro_customer_lastname: Type.Optional(Type.String()),
  voucher_items: Type.Optional(Type.Array(VoucherItemResponseSchema)),
  voucher_usages: Type.Optional(Type.Array(VoucherUsageResponseSchema)),
  created_at: Type.Optional(Type.String()),
  expiration_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'expiration_date' } }),
  ),
  created_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'created_at' } })),
  /** Un PDF de carte cadeau est disponible (généré pour les achats en ligne) → lien de téléchargement. */
  pdf_ready: Type.Optional(Type.Boolean()),
});
export type VoucherResponseObject = Static<typeof VoucherResponseSchema>;

export const VoucherListResponseSchema = Type.Object({
  total: Type.Number(),
  has_elements: Type.Boolean(),
  items: Type.Array(VoucherResponseSchema),
});
export type VoucherListResponseObject = Static<typeof VoucherListResponseSchema>;

// ─────────────────────────────────────────
// PUBLIC GIFT CARD PURCHASE (tunnel client final)
// ─────────────────────────────────────────

/**
 * Config publique du tunnel d'achat de carte cadeau / forfait, dérivée de `gift_card_settings`
 * + catalogue autorisé. Pilote l'UI (montant min/max, prestations/packs achetables). Aucun
 * `banner_image`/`display_text` (obsolètes) : le visuel vient des props du bloc website,
 * la carte PDF vient du template éditeur.
 */
export const GiftCardPublicConfigResponseSchema = Type.Object({
  professional_id: Type.Number(),
  professional_name: Type.String(),
  professional_logo: Type.Optional(Type.String()),
  currency: Type.String(),
  allow_amount: Type.Boolean(),
  amount_min: Type.Optional(Type.Number()),
  amount_max: Type.Optional(Type.Number()),
  allow_prestations: Type.Boolean(),
  allow_packs: Type.Boolean(),
  default_validity_months: Type.Number(),
  default_multiple_usage: Type.Boolean(),
  prestations: Type.Array(CatalogSelectionResponseSchema),
  /** Catégories des prestations achetables (pour les onglets de filtre + le regroupement). */
  categories: Type.Array(Type.Object({ catalog_category_id: Type.Number(), name: Type.String() })),
  packs: Type.Array(PackResponseSchema),
  /**
   * Tokens Stripe des moyens de paiement actifs sur le compte Connect du pro — affichage de la
   * réassurance (logos CB/Visa/Mastercard…) dans le tunnel. Absent si le compte ne peut pas
   * encaisser. Le checkout réel reste card-only (miroir du tunnel de réservation).
   */
  allowed_payment_methods: Type.Optional(Type.Array(Type.String())),
  /**
   * URL publique du PDF de CGV du pro (302 → présigné). Présent uniquement si le pro a uploadé un
   * PDF : le tunnel affiche alors une case « j'accepte les conditions » obligatoire au checkout.
   */
  terms_pdf_url: Type.Optional(Type.String()),
});
export type GiftCardPublicConfigResponseObject = Static<typeof GiftCardPublicConfigResponseSchema>;

/**
 * Renvoi manuel d'un email de carte cadeau (action pro depuis le détail du bon).
 * `target` choisit le destinataire : `BUYER` (email d'achat) ou `RECIPIENT` (email cadeau, bons
 * cadeaux uniquement). `recipient_email` corrige l'adresse du destinataire avant le renvoi.
 */
export const ResendGiftEmailRequestSchema = Type.Object({
  target: Type.Union([Type.Literal('BUYER'), Type.Literal('RECIPIENT')]),
  recipient_email: Type.Optional(Type.String({ format: 'email' })),
});
export type ResendGiftEmailRequestObject = Static<typeof ResendGiftEmailRequestSchema>;

/** Identité de l'acheteur anonyme (devient le `pro_customer` propriétaire du bon). */
export const GiftCardBuyerSchema = Type.Object({
  firstname: Type.String({ minLength: 1, maxLength: 100 }),
  lastname: Type.String({ minLength: 1, maxLength: 100 }),
  email: Type.String({ format: 'email', maxLength: 255 }),
  phone_number: Type.Optional(Type.String({ maxLength: 20 })),
});

/**
 * Achat public d'une carte cadeau / forfait. `is_gift` = choix de l'acheteur (toggle), pas de
 * la variante d'entrée. `multiple_usage`/`expiration_date` ne viennent PAS du client (dérivés
 * serveur depuis `gift_card_settings`). Le prix est recalculé côté serveur.
 */
export const GiftCardCheckoutRequestSchema = Type.Object({
  buyer: GiftCardBuyerSchema,
  type: VoucherTypeSchema(),
  pack_id: Type.Optional(Type.Integer({ minimum: 1 })),
  prestations: Type.Optional(Type.Array(VoucherItemInputSchema, { minItems: 1, maxItems: GIFT_CARD_MAX_ITEMS })),
  amount: Type.Optional(Type.Integer({ minimum: GIFT_CARD_AMOUNT_MIN_CENTS, meta: { is_price: true } })),
  is_gift: Type.Optional(Type.Boolean({ default: false })),
  gift_recipient_name: Type.Optional(Type.String({ maxLength: 100 })),
  gift_recipient_email: Type.Optional(Type.String({ maxLength: 255, format: 'email' })),
  gift_recipient_phone: Type.Optional(Type.String({ maxLength: 20 })),
  gift_message: Type.Optional(Type.String({ maxLength: 500 })),
  gift_send_date: Type.Optional(Type.String({ format: 'date-time' })),
});
export type GiftCardCheckoutRequestObject = Static<typeof GiftCardCheckoutRequestSchema>;

export const GiftCardCheckoutResponseSchema = Type.Object({
  voucher_uuid: Type.String(),
  payment_uuid: Type.String(),
  payment_url: Type.String(),
  /** Montant TTC de l'achat en centimes : celui du paiement à régler en ligne, en une fois. */
  amount: Type.Number(),
  /**
   * Lignes de l'achat pour l'événement de suivi `purchase` : le forfait (PACK), une ligne par
   * prestation offerte (PRESTATION) ou la carte en montant (AMOUNT). Leur somme vaut `amount`.
   */
  tracking_items: Type.Optional(Type.Array(TrackingItemSchema)),
  // Devise du pro (code ISO 4217, casse non garantie : cf. `resolveSiteEventCurrency`) : celle
  // de `amount`, reprise par l'événement de suivi `purchase`.
  currency: Type.Optional(Type.String()),
});
export type GiftCardCheckoutResponseObject = Static<typeof GiftCardCheckoutResponseSchema>;

/**
 * Aperçu du panier (mini-panier + total) du tunnel d'achat de carte cadeau. Le total est
 * **calculé ET formaté par le serveur** (règle prix : base + Σ suppléments × quantité), seule
 * source de vérité possible pour une prestation avec suppléments. Sous-ensemble du checkout
 * (sans l'acheteur). Miroir du `cart-summary` du tunnel de réservation.
 */
export const GiftCardCartSummaryRequestSchema = Type.Object({
  type: VoucherTypeSchema(),
  pack_id: Type.Optional(Type.Integer({ minimum: 1 })),
  prestations: Type.Optional(Type.Array(VoucherItemInputSchema, { minItems: 1, maxItems: GIFT_CARD_MAX_ITEMS })),
  amount: Type.Optional(Type.Integer({ minimum: 1 })),
});
export type GiftCardCartSummaryRequestObject = Static<typeof GiftCardCartSummaryRequestSchema>;

export const GiftCardCartSummaryItemSchema = Type.Object({
  label: Type.String(),
  /** Prix de la ligne (base × quantité), pré-formaté. */
  line_price: Type.String(),
  /** Mode de prix (FIXED | FROM | QUOTE) — pour afficher « Sur devis » / « À partir de ». */
  price_type: Type.Optional(Type.String()),
  quantity: Type.Optional(Type.Number()),
  supplements: Type.Array(
    Type.Object({
      name: Type.String(),
      price: Type.String(),
      /** Nombre de ce supplément sur la ligne, renseigné au-delà d'un seul. */
      quantity: Type.Optional(Type.Integer({ minimum: 2 })),
    }),
  ),
});
export type GiftCardCartSummaryItemObject = Static<typeof GiftCardCartSummaryItemSchema>;

export const GiftCardCartSummaryResponseSchema = Type.Object({
  items: Type.Array(GiftCardCartSummaryItemSchema),
  total_price: Type.String(),
  total_duration: Type.Optional(Type.String()),
});
export type GiftCardCartSummaryResponseObject = Static<typeof GiftCardCartSummaryResponseSchema>;

/** Détails publics du bon après paiement, pour la page de confirmation (récap + code + lien PDF). */
export const GiftCardConfirmationResponseSchema = Type.Object({
  voucher_uuid: Type.String(),
  status: Type.String(),
  unique_code: Type.Optional(Type.String()),
  is_gift: Type.Boolean(),
  gift_recipient_name: Type.Optional(Type.String()),
  /** Message du cadeau (si offert) — affiché dans le récap. */
  gift_message: Type.Optional(Type.String()),
  pdf_ready: Type.Boolean(),
  pdf_url: Type.Optional(Type.String()),
  expiration_date_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'expiration_date' } }),
  ),
  // ── Récapitulatif de l'achat ──────────────────────────────────────────
  /** Type de carte (AMOUNT | PACK | PRESTATION). */
  type: Type.String(),
  /** Montant formaté — uniquement pour une carte « montant libre » (AMOUNT). */
  amount_formatted: Type.Optional(Type.String({ meta: { is_price: true, source_key: 'amount_raw' } })),
  /** Nom du forfait (si type PACK). */
  pack_name: Type.Optional(Type.String()),
  /** Prestations incluses (PACK/PRESTATION) — nom + quantité. */
  items: Type.Optional(Type.Array(Type.Object({ name: Type.String(), quantity: Type.Number() }))),
});
export type GiftCardConfirmationResponseObject = Static<typeof GiftCardConfirmationResponseSchema>;

export const maxPackPerProfessional = 50;
